export const VIEW_ALL_DEALS_PERMISSION = 'view_all_deals';

/**
 * Working every deal, without being an admin.
 *
 * view_all_deals opens the tracker; this one lets somebody actually
 * work what they can see. The alternative was making a senior landman
 * an admin, which also hands over billing, tenant settings, user
 * accounts and everyone's lead claims.
 *
 * In the database this is one function — can_edit_deal — and it gates
 * insert, update AND delete on every table hanging off a deal: offers,
 * documents, todos, activities, closings, signature requests, ownership
 * records, certified mail. So this grant carries delete with it, the
 * same way it does for an admin.
 */
export const EDIT_ALL_DEALS_PERMISSION = 'edit_all_deals';

export function hasPermission(user, permission) {
  return Array.isArray(user?.permissions) && user.permissions.includes(permission);
}

/**
 * Can this person see every deal, or only the ones they are on?
 *
 * An admin always can. So can anyone granted `view_all_deals` — which
 * is how a senior landman gets the whole tracker without also being
 * given the keys to billing, tenant settings and everyone's claims.
 *
 * The database has always known this: the deals read policy asks
 * can_view_all_deals(), which checks the role OR the permission. The
 * browser asked only about the role, so the rows arrived and the screen
 * filtered them straight back out — the permission could be granted and
 * nothing whatsoever would change. Both halves ask the same question
 * now.
 */
export function canViewAllDeals(user) {
  return user?.role === 'admin'
    || hasPermission(user, VIEW_ALL_DEALS_PERMISSION)
    // Editing implies seeing. Without this, granting only edit_all_deals
    // leaves somebody able to write a deal the tracker will not show
    // them — the database makes the same implication.
    || hasPermission(user, EDIT_ALL_DEALS_PERMISSION);
}

export function canEditDeal(user, deal) {
  if (!user?.id || !deal) return false;
  if (user.role === 'admin') return true;
  if (hasPermission(user, EDIT_ALL_DEALS_PERMISSION)) return true;
  return (
    deal.createdBy === user.id
    || deal.buySideAgentId === user.id
    || deal.sellSideAgentId === user.id
    || deal.coordinatorId === user.id
  );
}
